const MAX_CHUNK_LENGTH = 4500;
const MIN_PREFERRED_CHUNK_LENGTH = 180;
const FENCED_CODE_MARKER = "```";
const MARKDOWN_CONTROL_CHARACTERS = new Set(["#", ">", "*", "_", "~", "|"]);

function collapseWhitespace(text: string): string {
   let result = "";
   let lastWasWhitespace = true;

   for (const char of text) {
      if (char.trim() === "") {
         if (!lastWasWhitespace) {
            result += " ";
         }
         lastWasWhitespace = true;
         continue;
      }

      result += char;
      lastWasWhitespace = false;
   }

   return result.trim();
}

function stripFencedCodeBlocks(text: string): string {
   let result = "";
   let index = 0;

   while (index < text.length) {
      if (!text.startsWith(FENCED_CODE_MARKER, index)) {
         result += text[index];
         index += 1;
         continue;
      }

      const closingIndex = text.indexOf(FENCED_CODE_MARKER, index + FENCED_CODE_MARKER.length);
      result += " ";
      index =
         closingIndex === -1
            ? text.length
            : closingIndex + FENCED_CODE_MARKER.length;
   }

   return result;
}

function stripInlineCodeMarkers(text: string): string {
   let result = "";
   let index = 0;

   while (index < text.length) {
      if (text[index] !== "`") {
         result += text[index];
         index += 1;
         continue;
      }

      const closingIndex = text.indexOf("`", index + 1);
      if (closingIndex === -1) {
         index += 1;
         continue;
      }

      result += text.slice(index + 1, closingIndex);
      index = closingIndex + 1;
   }

   return result;
}

function replaceMarkdownLinks(text: string): string {
   let result = "";
   let index = 0;

   while (index < text.length) {
      const isImage = text.startsWith("![", index);
      const isLink = text[index] === "[";
      if (!isImage && !isLink) {
         result += text[index];
         index += 1;
         continue;
      }

      const labelStart = index + (isImage ? 2 : 1);
      const labelEnd = text.indexOf("]", labelStart);
      const urlStart = labelEnd === -1 ? -1 : text.indexOf("(", labelEnd + 1);
      const urlEnd = urlStart === -1 ? -1 : text.indexOf(")", urlStart + 1);
      if (labelEnd === -1 || urlStart !== labelEnd + 1 || urlEnd === -1) {
         result += text[index];
         index += 1;
         continue;
      }

      if (isImage) {
         result += " ";
      } else {
         result += text.slice(labelStart, labelEnd);
      }
      index = urlEnd + 1;
   }

   return result;
}

function stripMarkdownControlCharacters(text: string): string {
   let result = "";

   for (const char of text) {
      result += MARKDOWN_CONTROL_CHARACTERS.has(char) ? " " : char;
   }

   return result;
}

function splitSentences(text: string): string[] {
   const sentences: string[] = [];
   let start = 0;
   let index = 0;

   while (index < text.length) {
      const char = text[index];
      if (char !== "." && char !== "!" && char !== "?") {
         index += 1;
         continue;
      }

      index += 1;
      while (index < text.length && ["\"", "'", ")", "]"].includes(text[index])) {
         index += 1;
      }

      sentences.push(text.slice(start, index));
      start = index;
   }

   if (start < text.length) {
      sentences.push(text.slice(start));
   }

   return sentences.length > 0 ? sentences : [text];
}

export function getSpeakableText(text: string): string {
   return collapseWhitespace(
      stripMarkdownControlCharacters(
         replaceMarkdownLinks(stripInlineCodeMarkers(stripFencedCodeBlocks(text)))
      )
   );
}

function getNaturalBreakIndex(text: string, maxLength: number, minBreakLength: number): number {
   const searchWindow = text.slice(0, maxLength);
   const sentenceBreakAt = Math.max(
      searchWindow.lastIndexOf(". "),
      searchWindow.lastIndexOf("? "),
      searchWindow.lastIndexOf("! ")
   );
   if (sentenceBreakAt > minBreakLength) {
      return sentenceBreakAt + 1;
   }

   const phraseBreakAt = Math.max(
      searchWindow.lastIndexOf(", "),
      searchWindow.lastIndexOf("; "),
      searchWindow.lastIndexOf(": "),
      searchWindow.lastIndexOf(" ")
   );

   return phraseBreakAt > minBreakLength ? phraseBreakAt + 1 : maxLength;
}

function splitLongText(text: string): string[] {
   const chunks: string[] = [];
   let remaining = text.trim();

   while (remaining.length > MAX_CHUNK_LENGTH) {
      const safeBreakAt = getNaturalBreakIndex(
         remaining,
         MAX_CHUNK_LENGTH,
         MIN_PREFERRED_CHUNK_LENGTH
      );

      chunks.push(remaining.slice(0, safeBreakAt).trim());
      remaining = remaining.slice(safeBreakAt).trim();
   }

   if (remaining) {
      chunks.push(remaining);
   }

   return chunks;
}

function appendSentenceToChunks(
   trimmedSentence: string,
   current: string,
   chunks: string[]
): string {
   if (trimmedSentence.length > MAX_CHUNK_LENGTH) {
      if (current) {
         chunks.push(current);
      }
      chunks.push(...splitLongText(trimmedSentence));
      return "";
   }

   const next = current ? `${current} ${trimmedSentence}` : trimmedSentence;
   if (next.length > MAX_CHUNK_LENGTH && current.length >= MIN_PREFERRED_CHUNK_LENGTH) {
      chunks.push(current);
      return trimmedSentence;
   }

   return next;
}

export function chunkTextForSpeech(text: string): string[] {
   const speakableText = getSpeakableText(text);
   if (!speakableText) {
      return [];
   }

   const sentences = splitSentences(speakableText);
   const chunks: string[] = [];
   let current = "";

   for (const sentence of sentences) {
      const trimmedSentence = sentence.trim();
      if (!trimmedSentence) {
         continue;
      }

      current = appendSentenceToChunks(trimmedSentence, current, chunks);
   }

   if (current) {
      chunks.push(current);
   }

   return chunks;
}
